import { useState, useEffect, useCallback } from "react";
import {
  MapPin, Search, CheckCircle, XCircle, Wrench, Calendar,
  BarChart2, Clock, RefreshCw, Globe, Shield, AlertTriangle,
  Loader2, X, Check, ChevronDown, ChevronUp,
} from "lucide-react";
import { countries as countriesApi } from "../lib/api.js";

const STATUS_META = {
  AVAILABLE:     { label: "Available",     badge: { background: "#d1fae5", color: "#065f46" }, dot: "#10b981", icon: CheckCircle },
  NOT_AVAILABLE: { label: "Not Available", badge: { background: "#fee2e2", color: "#991b1b" }, dot: "#ef4444", icon: XCircle    },
  MAINTENANCE:   { label: "Maintenance",   badge: { background: "#fef3c7", color: "#92400e" }, dot: "#f59e0b", icon: Wrench     },
  SCHEDULED:     { label: "Scheduled",     badge: { background: "#ede9fe", color: "#5b21b6" }, dot: "#8b5cf6", icon: Calendar   },
};

const SUMMARY_COLORS = {
  available:     { bg: "#10b981", icon: CheckCircle  },
  notAvailable:  { bg: "#ef4444", icon: XCircle      },
  maintenance:   { bg: "#f59e0b", icon: Wrench       },
  scheduled:     { bg: "#8b5cf6", icon: Calendar     },
  totalBlocked:  { bg: "#6b7280", icon: AlertTriangle},
};

function StatusBadge({ status }) {
  const m = STATUS_META[status] ?? STATUS_META.NOT_AVAILABLE;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 6,
      padding: "3px 10px", borderRadius: 20, fontSize: 12, fontWeight: 600,
      ...m.badge,
    }}>
      <span style={{ width: 7, height: 7, borderRadius: "50%", background: m.dot, flexShrink: 0 }} />
      {m.label}
    </span>
  );
}

function SummaryCard({ label, value, icon: Icon, color, onClick, active }) {
  return (
    <div onClick={onClick} style={{
      background: "var(--card)", borderRadius: "var(--r-lg)",
      boxShadow: active ? "0 0 0 2px var(--teal)" : "var(--sh)",
      border: active ? "1px solid var(--teal)" : "1px solid var(--border)",
      padding: "16px 18px",
      display: "flex", alignItems: "center", gap: 14,
      cursor: onClick ? "pointer" : "default",
      transition: "box-shadow .15s, border-color .15s",
      opacity: onClick ? 1 : 0.85,
    }}>
      <div style={{
        width: 44, height: 44, borderRadius: 12, background: color,
        display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
      }}>
        <Icon size={20} color="#fff" />
      </div>
      <div>
        <div style={{ fontSize: 24, fontWeight: 800, color: "var(--ink)", lineHeight: 1.1 }}>{value ?? "—"}</div>
        <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 2 }}>{label}</div>
      </div>
    </div>
  );
}

function EditModal({ country, onClose, onSave, loading }) {
  const [status, setStatus]         = useState(country.status);
  const [launchDate, setLaunchDate] = useState(country.launchDate ? country.launchDate.slice(0, 16) : "");
  const [notes, setNotes]           = useState(country.notes ?? "");

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 50, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(0,0,0,.5)", padding: 16 }}>
      <div style={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: "var(--r-lg)", width: "100%", maxWidth: 440, padding: 24, boxShadow: "var(--sh-lg)" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18 }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: "var(--ink)" }}>{country.countryFlag} {country.countryName}</div>
            <div style={{ fontSize: 12, color: "var(--muted)" }}>{country.countryCode}</div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--muted)", padding: 4, borderRadius: 6, display: "flex" }}><X size={18} /></button>
        </div>

        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: "var(--ink)", marginBottom: 8 }}>Status</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            {Object.entries(STATUS_META).map(([key, m]) => {
              const active = status === key;
              return (
                <button key={key} onClick={() => setStatus(key)} style={{
                  display: "flex", alignItems: "center", gap: 8, padding: "10px 12px",
                  borderRadius: 10, border: `2px solid ${active ? "var(--teal)" : "var(--border)"}`,
                  background: active ? "var(--teal-dim)" : "transparent",
                  color: active ? "var(--teal)" : "var(--muted)",
                  cursor: "pointer", fontWeight: 600, fontSize: 13, transition: "all .15s",
                }}>
                  <m.icon size={14} /> {m.label}
                </button>
              );
            })}
          </div>
        </div>

        {status === "SCHEDULED" && (
          <div style={{ marginBottom: 16 }}>
            <label className="f-lbl">Launch Date &amp; Time</label>
            <input type="datetime-local" value={launchDate} onChange={e => setLaunchDate(e.target.value)} className="f-in" />
          </div>
        )}

        <div style={{ marginBottom: 20 }}>
          <label className="f-lbl">Notes (optional)</label>
          <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2}
            placeholder="Internal notes..." className="f-ta" />
        </div>

        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={onClose} className="btn btn-out" style={{ flex: 1, justifyContent: "center" }}>Cancel</button>
          <button onClick={() => onSave(country.id, { status, launchDate: status === "SCHEDULED" && launchDate ? launchDate : null, notes })}
            disabled={loading} className="btn btn-teal" style={{ flex: 1, justifyContent: "center" }}>
            {loading ? <Loader2 size={14} style={{ animation: "spin 1s linear infinite" }} /> : <Check size={14} />}
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
}

function AuditModal({ country, onClose }) {
  const [audit, setAudit]     = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    countriesApi.audit(country.id).then(r => setAudit(r.audit ?? [])).catch(() => {}).finally(() => setLoading(false));
  }, [country.id]);

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 50, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(0,0,0,.5)", padding: 16 }}>
      <div style={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: "var(--r-lg)", width: "100%", maxWidth: 560, padding: 24, boxShadow: "var(--sh-lg)", display: "flex", flexDirection: "column", maxHeight: "80vh" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: "var(--ink)" }}>{country.countryFlag} {country.countryName} — Audit History</div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--muted)", padding: 4, borderRadius: 6, display: "flex" }}><X size={18} /></button>
        </div>
        <div style={{ overflowY: "auto", flex: 1 }}>
          {loading ? (
            <div style={{ display: "flex", justifyContent: "center", padding: 40 }}><Loader2 size={22} style={{ color: "var(--teal)", animation: "spin 1s linear infinite" }} /></div>
          ) : audit.length === 0 ? (
            <p style={{ textAlign: "center", color: "var(--muted)", padding: 40 }}>No audit history yet.</p>
          ) : audit.map(a => (
            <div key={a.id} style={{ display: "flex", gap: 10, padding: "10px 12px", borderRadius: 10, background: "var(--surface)", marginBottom: 8 }}>
              <Clock size={14} style={{ color: "var(--muted)", marginTop: 3, flexShrink: 0 }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: "var(--ink)" }}>{a.action}</div>
                {a.prevStatus && a.newStatus && (
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 4 }}>
                    <StatusBadge status={a.prevStatus} /> <span style={{ color: "var(--muted)", fontSize: 12 }}>→</span> <StatusBadge status={a.newStatus} />
                  </div>
                )}
                <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 4 }}>
                  {a.performedBy ? `By ${a.performedBy.name}` : "Auto"} · {new Date(a.createdAt).toLocaleString()}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function BulkModal({ selected, countries, onClose, onConfirm, loading }) {
  const [action, setAction] = useState("AVAILABLE");
  const [date, setDate]     = useState("");
  const selectedNames = countries.filter(c => selected.has(c.id)).map(c => c.countryFlag + " " + c.countryName);

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 50, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(0,0,0,.5)", padding: 16 }}>
      <div style={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: "var(--r-lg)", width: "100%", maxWidth: 440, padding: 24, boxShadow: "var(--sh-lg)" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: "var(--ink)" }}>Bulk Update — {selected.size} countries</div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--muted)", padding: 4, borderRadius: 6, display: "flex" }}><X size={18} /></button>
        </div>

        <div style={{ maxHeight: 120, overflowY: "auto", fontSize: 13, color: "var(--muted)", marginBottom: 16, background: "var(--surface)", borderRadius: 8, padding: "8px 12px" }}>
          {selectedNames.map((n, i) => <div key={i} style={{ padding: "2px 0" }}>{n}</div>)}
        </div>

        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: "var(--ink)", marginBottom: 8 }}>Action</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            {Object.entries(STATUS_META).map(([key, m]) => {
              const active = action === key;
              return (
                <button key={key} onClick={() => setAction(key)} style={{
                  display: "flex", alignItems: "center", gap: 8, padding: "10px 12px",
                  borderRadius: 10, border: `2px solid ${active ? "var(--teal)" : "var(--border)"}`,
                  background: active ? "var(--teal-dim)" : "transparent",
                  color: active ? "var(--teal)" : "var(--muted)",
                  cursor: "pointer", fontWeight: 600, fontSize: 13, transition: "all .15s",
                }}>
                  <m.icon size={14} /> {m.label}
                </button>
              );
            })}
          </div>
        </div>

        {action === "SCHEDULED" && (
          <div style={{ marginBottom: 16 }}>
            <label className="f-lbl">Launch Date</label>
            <input type="datetime-local" value={date} onChange={e => setDate(e.target.value)} className="f-in" />
          </div>
        )}

        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={onClose} className="btn btn-out" style={{ flex: 1, justifyContent: "center" }}>Cancel</button>
          <button onClick={() => { const body = { ids: [...selected], status: action }; if (action === "SCHEDULED" && date) body.launchDate = date; onConfirm(body); }}
            disabled={loading} className="btn btn-teal" style={{ flex: 1, justifyContent: "center" }}>
            {loading ? <Loader2 size={14} style={{ animation: "spin 1s linear infinite" }} /> : <Check size={14} />}
            Apply to {selected.size} countries
          </button>
        </div>
      </div>
    </div>
  );
}

export default function PageCountryAvailability({ toast, hasPermission }) {
  const canManage = hasPermission("MANAGE_COUNTRIES");

  const [countries, setCountries]       = useState([]);
  const [analytics, setAnalytics]       = useState(null);
  const [loading, setLoading]           = useState(true);
  const [saving, setSaving]             = useState(false);
  const [search, setSearch]             = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [sortBy, setSortBy]             = useState("countryName");
  const [sortDir, setSortDir]           = useState("asc");
  const [tab, setTab]                   = useState("list");
  const [editCountry, setEditCountry]   = useState(null);
  const [auditCountry, setAuditCountry] = useState(null);
  const [selected, setSelected]         = useState(new Set());
  const [showBulk, setShowBulk]         = useState(false);
  const [page, setPage]                 = useState(1);
  const [total, setTotal]               = useState(0);
  const LIMIT = 50;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [listRes, analyticsRes] = await Promise.allSettled([
        countriesApi.list({ search, status: statusFilter, sortBy, sortDir, page, limit: LIMIT }),
        countriesApi.analytics(),
      ]);
      if (listRes.status === "fulfilled") { setCountries(listRes.value.countries ?? []); setTotal(listRes.value.total ?? 0); }
      if (analyticsRes.status === "fulfilled") setAnalytics(analyticsRes.value);
    } finally { setLoading(false); }
  }, [search, statusFilter, sortBy, sortDir, page]);

  useEffect(() => { load(); }, [load]);

  const handleSort = (col) => {
    if (sortBy === col) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortBy(col); setSortDir("asc"); }
  };

  const SortIcon = ({ col }) => sortBy === col
    ? (sortDir === "asc" ? <ChevronUp size={12} style={{ color: "var(--teal)" }} /> : <ChevronDown size={12} style={{ color: "var(--teal)" }} />)
    : <ChevronDown size={12} style={{ color: "var(--muted)" }} />;

  const handleSave = async (id, data) => {
    setSaving(true);
    try { await countriesApi.update(id, data); toast("Country updated.", "success"); setEditCountry(null); load(); }
    catch { toast("Failed to update country.", "error"); }
    finally { setSaving(false); }
  };

  const handleBulk = async (body) => {
    setSaving(true);
    try { const res = await countriesApi.bulk(body); toast(`Updated ${res.succeeded} countries.`, "success"); setShowBulk(false); setSelected(new Set()); load(); }
    catch { toast("Bulk update failed.", "error"); }
    finally { setSaving(false); }
  };

  const toggleSelect = (id) => setSelected(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => selected.size === countries.length ? setSelected(new Set()) : setSelected(new Set(countries.map(c => c.id)));

  const quickAction = async (country, status) => {
    setSaving(true);
    try { await countriesApi.update(country.id, { status }); toast(`${country.countryName} → ${STATUS_META[status].label}.`, "success"); load(); }
    catch { toast("Update failed.", "error"); }
    finally { setSaving(false); }
  };

  // map card key → status filter value (null means no filter)
  const CARD_FILTER = {
    available:    "AVAILABLE",
    notAvailable: "NOT_AVAILABLE",
    maintenance:  "MAINTENANCE",
    scheduled:    "SCHEDULED",
    totalBlocked: null,
  };

  const summaryItems = analytics ? [
    { key: "available",    label: "Available",               value: analytics.summary.available    },
    { key: "notAvailable", label: "Not Available",           value: analytics.summary.notAvailable },
    { key: "maintenance",  label: "Maintenance",             value: analytics.summary.maintenance  },
    { key: "scheduled",    label: "Scheduled",               value: analytics.summary.scheduled    },
    { key: "totalBlocked", label: "Blocked Requests (total)",value: analytics.summary.totalBlocked },
  ] : [];

  return (
    <div>
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 20 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: "var(--ink)", display: "flex", alignItems: "center", gap: 8, margin: 0 }}>
            <MapPin size={22} style={{ color: "var(--teal)" }} /> Country Availability
          </h1>
          <p style={{ fontSize: 13, color: "var(--muted)", margin: "4px 0 0" }}>Control where the mobile application is accessible</p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={() => setTab(t => t === "list" ? "analytics" : "list")} className="btn btn-out" style={{ display: "flex", alignItems: "center", gap: 6 }}>
            {tab === "list" ? <><BarChart2 size={14} /> Analytics</> : <><Globe size={14} /> Countries</>}
          </button>
          {selected.size > 0 && canManage && (
            <button onClick={() => setShowBulk(true)} className="btn btn-teal" style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <Shield size={14} /> Bulk Update ({selected.size})
            </button>
          )}
          <button onClick={load} className="btn btn-out" title="Refresh" style={{ padding: "0 10px" }}>
            <RefreshCw size={14} style={{ animation: loading ? "spin 1s linear infinite" : "none" }} />
          </button>
        </div>
      </div>

      {/* ── Summary cards ──────────────────────────────────────────────────── */}
      {analytics && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 14, marginBottom: 20 }}>
          {summaryItems.map(s => {
            const filterVal = CARD_FILTER[s.key];
            const clickable = filterVal !== null;
            const active = clickable && statusFilter === filterVal && tab === "list";
            return (
              <SummaryCard key={s.key} label={s.label} value={s.value}
                icon={SUMMARY_COLORS[s.key].icon} color={SUMMARY_COLORS[s.key].bg}
                active={active}
                onClick={clickable ? () => {
                  setTab("list");
                  setStatusFilter(active ? "" : filterVal);
                  setPage(1);
                } : undefined}
              />
            );
          })}
        </div>
      )}

      {tab === "analytics" ? (
        /* ── Analytics tab ─────────────────────────────────────────────────── */
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 18 }}>
          {analytics && (<>
            <div className="card" style={{ marginBottom: 0, padding: "18px 20px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14, fontWeight: 700, color: "var(--ink)" }}>
                <BarChart2 size={15} style={{ color: "var(--teal)" }} /> Blocked Requests
              </div>
              {[{ label: "Today", val: analytics.blockedRequests.daily }, { label: "This Week", val: analytics.blockedRequests.weekly }, { label: "This Month", val: analytics.blockedRequests.monthly }].map(r => (
                <div key={r.label} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "7px 0", borderBottom: "1px solid var(--border)" }}>
                  <span style={{ fontSize: 13, color: "var(--muted)" }}>{r.label}</span>
                  <span style={{ fontWeight: 700, color: "var(--ink)" }}>{r.val?.toLocaleString()}</span>
                </div>
              ))}
            </div>

            <div className="card" style={{ marginBottom: 0, padding: "18px 20px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14, fontWeight: 700, color: "var(--ink)" }}>
                <Globe size={15} style={{ color: "var(--teal)" }} /> Top Requesting Countries
              </div>
              {analytics.topCountries.length === 0 ? (
                <p style={{ textAlign: "center", color: "var(--muted)", padding: "20px 0" }}>No blocked requests yet.</p>
              ) : analytics.topCountries.map((c, i) => (
                <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "7px 0", borderBottom: "1px solid var(--border)" }}>
                  <span style={{ fontSize: 13, color: "var(--ink)" }}>{c.countryName ?? c.countryCode ?? "Unknown"}</span>
                  <span style={{ fontWeight: 700, color: "var(--ink)" }}>{c.attempts?.toLocaleString()}</span>
                </div>
              ))}
            </div>

            <div className="card" style={{ marginBottom: 0, padding: "18px 20px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14, fontWeight: 700, color: "var(--ink)" }}>
                <Shield size={15} style={{ color: "var(--teal)" }} /> Blocked by Type
              </div>
              {analytics.byType.length === 0 ? (
                <p style={{ textAlign: "center", color: "var(--muted)", padding: "20px 0" }}>No data yet.</p>
              ) : analytics.byType.map((t, i) => (
                <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "7px 0", borderBottom: "1px solid var(--border)" }}>
                  <span style={{ fontSize: 13, color: "var(--ink)", textTransform: "capitalize" }}>{t.type.replace(/_/g, " ")}</span>
                  <span style={{ fontWeight: 700, color: "var(--ink)" }}>{t.count?.toLocaleString()}</span>
                </div>
              ))}
            </div>
          </>)}
        </div>
      ) : (
        /* ── Country list tab ──────────────────────────────────────────────── */
        <div className="card" style={{ marginBottom: 0, overflow: "hidden" }}>
          {/* Filters */}
          <div style={{ padding: "12px 16px", borderBottom: "1px solid var(--border)", display: "flex", flexWrap: "wrap", gap: 10 }}>
            <div style={{ position: "relative", flex: 1, minWidth: 200 }}>
              <Search size={14} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "var(--muted)" }} />
              <input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
                placeholder="Search countries..." className="f-in" style={{ paddingLeft: 32, width: "100%", boxSizing: "border-box" }} />
            </div>
            <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }} className="f-sel" style={{ width: 160 }}>
              <option value="">All Statuses</option>
              {Object.entries(STATUS_META).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
            </select>
            {(search || statusFilter) && (
              <button onClick={() => { setSearch(""); setStatusFilter(""); setPage(1); }} className="btn btn-out" style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <X size={13} /> Clear
              </button>
            )}
          </div>

          {/* Table */}
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ background: "var(--surface)", borderBottom: "1px solid var(--border)" }}>
                  {canManage && (
                    <th style={{ padding: "10px 14px", textAlign: "left", width: 40 }}>
                      <input type="checkbox" checked={selected.size === countries.length && countries.length > 0} onChange={toggleAll} />
                    </th>
                  )}
                  {[["countryName","COUNTRY"],["status","STATUS"],["launchDate","LAUNCH DATE"],["updatedAt","LAST UPDATED"]].map(([col, label]) => (
                    <th key={col} onClick={() => handleSort(col)}
                      style={{ padding: "10px 14px", textAlign: "left", fontWeight: 700, fontSize: 11, color: "var(--muted)", letterSpacing: ".5px", cursor: "pointer", userSelect: "none" }}>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>{label} <SortIcon col={col} /></span>
                    </th>
                  ))}
                  <th style={{ padding: "10px 14px", textAlign: "left", fontWeight: 700, fontSize: 11, color: "var(--muted)", letterSpacing: ".5px" }}>UPDATED BY</th>
                  {canManage && <th style={{ padding: "10px 14px", textAlign: "right", fontWeight: 700, fontSize: 11, color: "var(--muted)", letterSpacing: ".5px" }}>ACTIONS</th>}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={7} style={{ padding: "48px 0", textAlign: "center" }}>
                    <Loader2 size={22} style={{ color: "var(--teal)", animation: "spin 1s linear infinite", margin: "0 auto", display: "block" }} />
                  </td></tr>
                ) : countries.length === 0 ? (
                  <tr><td colSpan={7} style={{ padding: "48px 0", textAlign: "center", color: "var(--muted)" }}>No countries found.</td></tr>
                ) : countries.map(c => (
                  <tr key={c.id} style={{ borderBottom: "1px solid var(--border)", background: selected.has(c.id) ? "var(--teal-dim)" : "transparent", transition: "background .1s" }}>
                    {canManage && (
                      <td style={{ padding: "10px 14px" }}>
                        <input type="checkbox" checked={selected.has(c.id)} onChange={() => toggleSelect(c.id)} />
                      </td>
                    )}
                    <td style={{ padding: "10px 14px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10, fontWeight: 600, color: "var(--ink)" }}>
                        <span style={{ fontSize: 20 }}>{c.countryFlag}</span>
                        <div>
                          <div>{c.countryName}</div>
                          <div style={{ fontSize: 11, color: "var(--muted)", fontWeight: 400 }}>{c.countryCode}</div>
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: "10px 14px" }}><StatusBadge status={c.status} /></td>
                    <td style={{ padding: "10px 14px", color: "var(--muted)", fontSize: 12 }}>
                      {c.launchDate ? new Date(c.launchDate).toLocaleString() : <span style={{ color: "var(--ghost)" }}>—</span>}
                    </td>
                    <td style={{ padding: "10px 14px", color: "var(--muted)", fontSize: 12, whiteSpace: "nowrap" }}>
                      {new Date(c.updatedAt).toLocaleDateString()}
                    </td>
                    <td style={{ padding: "10px 14px", color: "var(--muted)", fontSize: 12 }}>
                      {c.updatedBy?.name ?? <span style={{ color: "var(--ghost)" }}>—</span>}
                    </td>
                    {canManage && (
                      <td style={{ padding: "10px 14px" }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 4 }}>
                          {c.status !== "AVAILABLE"     && <button onClick={() => quickAction(c, "AVAILABLE")}     title="Enable"      style={iconBtn("#10b981")}><CheckCircle size={14} /></button>}
                          {c.status !== "NOT_AVAILABLE" && <button onClick={() => quickAction(c, "NOT_AVAILABLE")} title="Disable"     style={iconBtn("#ef4444")}><XCircle size={14} /></button>}
                          {c.status !== "MAINTENANCE"   && <button onClick={() => quickAction(c, "MAINTENANCE")}   title="Maintenance" style={iconBtn("#f59e0b")}><Wrench size={14} /></button>}
                          <button onClick={() => setEditCountry(c)}   title="Edit"      style={iconBtn("var(--teal)")}><Calendar size={14} /></button>
                          <button onClick={() => setAuditCountry(c)}  title="Audit log" style={iconBtn("var(--muted)")}><Clock size={14} /></button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {total > LIMIT && (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 16px", borderTop: "1px solid var(--border)" }}>
              <span style={{ fontSize: 13, color: "var(--muted)" }}>
                {((page - 1) * LIMIT) + 1}–{Math.min(page * LIMIT, total)} of {total} countries
              </span>
              <div style={{ display: "flex", gap: 8 }}>
                <button disabled={page === 1} onClick={() => setPage(p => p - 1)} className="btn btn-out" style={{ opacity: page === 1 ? .4 : 1 }}>Previous</button>
                <button disabled={page * LIMIT >= total} onClick={() => setPage(p => p + 1)} className="btn btn-out" style={{ opacity: page * LIMIT >= total ? .4 : 1 }}>Next</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Modals ─────────────────────────────────────────────────────────── */}
      {editCountry  && <EditModal country={editCountry} onClose={() => setEditCountry(null)} onSave={handleSave} loading={saving} />}
      {auditCountry && <AuditModal country={auditCountry} onClose={() => setAuditCountry(null)} />}
      {showBulk     && <BulkModal selected={selected} countries={countries} onClose={() => setShowBulk(false)} onConfirm={handleBulk} loading={saving} />}
    </div>
  );
}

function iconBtn(color) {
  return {
    background: "none", border: "none", cursor: "pointer", color,
    padding: "5px 6px", borderRadius: 6, display: "flex", alignItems: "center",
    transition: "background .1s",
  };
}
